/**
 * How a history timestamp reads, in one place.
 *
 * The rule: minutes or hours for the first 24 hours, days for the rest of the
 * first week, then the plain date (DD-MM-YYYY).
 *
 * `core_project/templatetags/relative_time.py` is Django's copy of the same
 * rule — change one and change the other, and keep the boundary tests on both
 * sides in step. Before this existed the two drifted: Django's `timesince` gave
 * two units ("1 mois, 2 semaines") and floored anything sub-minute to
 * "0 minutes", while `Intl.RelativeTimeFormat` gave one unit and "maintenant".
 */

import i18n from "@/lib/i18n/i18n";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/** DD-MM-YYYY, matching the Django filter's fallback. */
function asDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(value.getDate())}-${pad(value.getMonth() + 1)}-${value.getFullYear()}`;
}

export function relativeTime(iso: string, lang: string): string {
  const value = new Date(iso);
  // Guard a malformed timestamp rather than rendering "Invalid Date".
  if (Number.isNaN(value.getTime())) return "";

  const seconds = (Date.now() - value.getTime()) / 1000;
  // `numeric: "always"` on purpose: "auto" renders exactly one day as
  // "hier" / "yesterday", where the Django filter says "il y a 1 jour". Same
  // rule, two wordings for the same timestamp — so the idiomatic form is given
  // up to keep the two apps saying the same thing.
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "always" });

  // A clock skew or a freshly written row can land marginally in the future;
  // read that as "now" rather than as a negative age. Not via `rtf`: with
  // `numeric: "always"` a zero renders as "dans 0 seconde".
  if (seconds < MINUTE) {
    // Resolved against the `lang` we were given, not the ambient language, so
    // the whole function speaks one language.
    return i18n.getFixedT(lang)("common.history.justNow");
  }
  if (seconds < HOUR) return rtf.format(-Math.floor(seconds / MINUTE), "minute");
  if (seconds < DAY) return rtf.format(-Math.floor(seconds / HOUR), "hour");
  if (seconds < WEEK) return rtf.format(-Math.floor(seconds / DAY), "day");
  return asDate(value);
}
